import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { api } from './api';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import DuelScorePage from './pages/DuelScorePage';
import ArchivePage from './pages/ArchivePage';
import DuelboardPage from './pages/DuelboardPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AdminPanel from './pages/AdminPanel';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import PrivacyPage from './pages/PrivacyPage';
import SyncingScreen from './components/SyncingScreen';
import Footer from './components/Footer';
import PwaBanners from './components/PwaBanners';
import './App.css';

type Page = 'dashboard' | 'duel' | 'archive' | 'duelboard' | 'leaderboard' | 'admin' | 'settings' | 'privacy' | 'profile';

function AppContent() {
  const { token, syncing, finishSync } = useAuth();
  const [updateReady, setUpdateReady] = useState(false);
  const getPageFromHash = (): Page => {
    if (window.location.hash.startsWith('#admin')) return 'admin';
    if (window.location.hash.startsWith('#privacy')) return 'privacy';
    return 'dashboard';
  };
  const [page, setPage] = useState<Page>(getPageFromHash);
  const [replayMatchupId, setReplayMatchupId] = useState<number | undefined>(undefined);
  const [viewPlayerId, setViewPlayerId] = useState<number | null>(null);

  // Scroll to top on every page change (incl. back/forward)
  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  // Browser-History: Bei Navigation State pushen
  const navigate = useCallback((newPage: Page) => {
    setPage(newPage);
    if (newPage === 'admin') {
      window.history.pushState({ page: newPage }, '', '#admin');
    } else if (newPage !== 'dashboard') {
      window.history.pushState({ page: newPage }, '', '');
    } else {
      window.history.replaceState({ page: 'dashboard' }, '', '');
    }
  }, []);

  // Browser Zurück-Button + Hash-Änderungen
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      // Hash hat Vorrang vor History-State
      const fromHash = getPageFromHash();
      if (fromHash !== 'dashboard') {
        setPage(fromHash);
        return;
      }
      const target = e.state?.page ?? 'dashboard';
      setPage(target as Page);
    };
    const handleHashChange = () => {
      setPage(getPageFromHash());
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Listen for Service Worker update events:
  // When SW_UPDATED arrives, auto-reload to show new version
  useEffect(() => {
    const handleSWUpdate = () => {
      // Small delay to let the new SW fully take over
      setTimeout(() => window.location.reload(), 500);
    };
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SW_UPDATED') {
        handleSWUpdate();
      }
    };

    window.addEventListener('sw-update-ready', handleSWUpdate);
    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('sw-update-ready', handleSWUpdate);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, []);

  if (!token) {
    if (page === 'privacy') return <><PrivacyPage onBack={() => { window.history.back(); }} /><PwaBanners updateReady={updateReady} /></>;
    return <><LoginPage /><PwaBanners updateReady={updateReady} /></>;
  }

  if (syncing) {
    return <><SyncingScreen onDone={finishSync} /><PwaBanners updateReady={updateReady} /></>;
  }

  if (page === 'duel') {
    return <><DuelScorePage onBack={() => { setReplayMatchupId(undefined); navigate('dashboard'); }} matchupId={replayMatchupId} onNavigateToPlayerProfile={(id) => { setViewPlayerId(id); navigate('profile'); }} /><Footer /><PwaBanners updateReady={updateReady} /></>;
  }

  if (page === 'archive') {
    return <><ArchivePage onBack={() => navigate('dashboard')} onNavigateToPlayerProfile={(id) => { setViewPlayerId(id); navigate('profile'); }} onReplay={(id) => {
      if (token) api.post('/duel/result/seen', { matchup_id: id }, token);
      setReplayMatchupId(id);
      navigate('duel');
    }} /><Footer /><PwaBanners updateReady={updateReady} /></>;
  }

  if (page === 'leaderboard') {
    return <><LeaderboardPage onBack={() => navigate('dashboard')} /><Footer /><PwaBanners updateReady={updateReady} /></>;
  }

  if (page === 'duelboard') {
    return <><DuelboardPage onBack={() => navigate('dashboard')} onNavigateToPlayerProfile={(id) => { setViewPlayerId(id); navigate('profile'); }} /><Footer /><PwaBanners updateReady={updateReady} /></>;
  }

  if (page === 'admin') {
    return <><AdminPanel onBack={() => navigate('dashboard')} /><Footer /><PwaBanners updateReady={updateReady} /></>;
  }

  if (page === 'settings') {
    return <><SettingsPage onBack={() => navigate('dashboard')} /><PwaBanners updateReady={updateReady} /></>;
  }

  if (page === 'profile') {
    return <><ProfilePage onBack={() => { setViewPlayerId(null); navigate('dashboard'); }} viewPlayerId={viewPlayerId} /><Footer /><PwaBanners updateReady={updateReady} /></>;
  }

  return <><DashboardPage
    onNavigateToDuel={() => navigate('duel')}
    onReplayMatchup={(id) => { setReplayMatchupId(id); navigate('duel'); }}
    onNavigateToArchive={() => navigate('archive')}
    onNavigateToDuelboard={() => navigate('duelboard')}
    onNavigateToLeaderboard={() => navigate('leaderboard')}
    onNavigateToSettings={() => navigate('settings')}
    onNavigateToProfile={() => { setViewPlayerId(null); navigate('profile'); }}
    onNavigateToPlayerProfile={(id) => { setViewPlayerId(id); navigate('profile'); }}
  /><PwaBanners updateReady={updateReady} /></>;
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
